import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import dayjs from "dayjs";
import {
  getReport,
  addItem,
  delItem,
  submitReport,
  checkReceipt,
  addReceipt,
} from "../../api";
import "./ReportDetail.css";

const fakeActorId = 7;

function prettyBytes(n) {
  if (!n && n !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let value = n;

  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }

  return `${value.toFixed(i ? 1 : 0)} ${units[i]}`;
}

// Presentational-only helpers: map a report/history status code or expense
// category string to one of the app's shared chip tones (green / blue /
// amber / red / gray / purple). No business logic -- purely which color a
// value is displayed in.
function statusTone(value) {
  const v = String(value || "").toUpperCase();

  if (["PAID", "APPROVED_FOR_PAYMENT", "MANAGER_APPROVED", "APPROVED"].includes(v)) {
    return "green";
  }

  if (["REJECTED", "CANCELLED", "DECLINED"].includes(v)) {
    return "red";
  }

  if (["REQUEST_CHANGES", "PENDING", "UNDER_FINANCE_REVIEW"].includes(v)) {
    return "amber";
  }

  if (["SUBMITTED", "DRAFT"].includes(v)) {
    return "blue";
  }

  return "gray";
}

const CATEGORY_TONES = {
  Travel: "blue",
  Hotel: "purple",
  Food: "green",
  Local: "amber",
  Misc: "gray",
};

function categoryTone(category) {
  return CATEGORY_TONES[category] || "gray";
}

export default function ReportDetail() {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [adding, setAdding] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [modal, setModal] = useState({
    open: false,
    url: null,
    name: "",
    type: "",
  });

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const objectUrlRef = useRef(null);

  const [item, setItem] = useState({
    expense_date: "",
    category: "Travel",
    vendor_name: "",
    description: "",
    quantity: 1,
    rate: 0,
    amount: 0,
    tax_rate_pct: 0,
    tax_amount: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await getReport(id);
      setData(data);
    } catch (error) {
      alert(error?.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const r = data?.report;
  const items = data?.items || [];
  const files = data?.files || [];
  const history = data?.history || [];

  const canEdit = useMemo(() => {
    return r && (r.status_code === "DRAFT" || r.status_code === "REQUEST_CHANGES");
  }, [r]);

  const resetItem = () => {
    setItem({
      expense_date: "",
      category: "Travel",
      vendor_name: "",
      description: "",
      quantity: 1,
      rate: 0,
      amount: 0,
      tax_rate_pct: 0,
      tax_amount: 0,
    });
  };

  const addItemClick = async () => {
    setAdding(true);

    try {
      const payload = {
        ...item,
        quantity: Number(item.quantity || 1),
        rate: Number(item.rate || 0),
        amount: Number(item.amount || 0),
        tax_rate_pct: Number(item.tax_rate_pct || 0),
        tax_amount: Number(item.tax_amount || 0),
      };

      await addItem(id, payload);
      resetItem();
      await load();
    } catch (error) {
      alert(error?.response?.data?.error || error.message);
    } finally {
      setAdding(false);
    }
  };

  const deleteItemClick = async (itemId) => {
    if (!window.confirm("Delete this item?")) return;

    try {
      await delItem(id, itemId);
      await load();
    } catch (error) {
      alert(error?.response?.data?.error || error.message);
    }
  };

  const submitClick = async () => {
    setSubmitting(true);

    try {
      await submitReport(id, { actor_id: fakeActorId });
      await load();
    } catch (error) {
      alert(error?.response?.data?.error || error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onFileSelect = async (file) => {
    const okType = /image\/(png|jpeg|jpg)|application\/pdf/i.test(file.type);

    if (!okType) {
      alert("Please upload a PNG/JPG image or a PDF.");
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      alert("File is larger than 8 MB.");
      return;
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;

    setPreviewUrl(url);
    setPreviewFile(file);
    setChecking(true);

    try {
      const report = await checkReceipt(id, file);
      setCheckResult(report);
    } catch (error) {
      alert(error?.response?.data?.error || error.message);
    } finally {
      setChecking(false);
    }
  };

  const clearPreview = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    objectUrlRef.current = null;
    setPreviewUrl(null);
    setPreviewFile(null);
    setCheckResult(null);
    setChecking(false);
  };

  const attachReceipt = async () => {
    if (!previewFile) return;

    if (checkResult?.issues?.length) {
      const message =
        `We found potential issues:\n\n- ${checkResult.issues.join("\n- ")}\n\n` +
        `Detected:\n  Date: ${checkResult.fields?.date || "-"}\n  Amount: ${
          checkResult.fields?.amount || "-"
        }\n  Vendor: ${checkResult.fields?.vendor || "-"}`;

      const proceed = window.confirm(
        message + "\n\nDo you still want to attach this receipt?"
      );

      if (!proceed) return;
    }

    setAttaching(true);

    try {
      await addReceipt(id, {
        file_name: previewFile.name,
        mime_type: previewFile.type,
        storage_path: `/local/${previewFile.name}`,
        uploaded_by: fakeActorId,
      });

      clearPreview();
      await load();
    } catch (error) {
      alert(error?.response?.data?.error || error.message);
    } finally {
      setAttaching(false);
    }
  };

  const getHistStatus = (h) => {
    return h?.to_status ?? h?.status_code ?? h?.status ?? h?.state ?? "-";
  };

  const statusLabel = (value) => {
    return String(value || "-").replaceAll("_", " ");
  };

  const totalAmount = Number(r?.amount || 0).toFixed(2);

  if (loading) {
    return (
      <div className="unit-page acct-reportdetail-theme">
        <div className="unit-loader">
          <div className="spinner-border" role="status"></div>
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  if (!data || !r) {
    return (
      <div className="unit-page acct-reportdetail-theme">
        <div className="unit-empty">
          <div className="unit-empty-icon">!</div>
          <h5>Report not found</h5>
          <p>The requested report could not be loaded.</p>
          <Link to="/admin/accounts/my-reports" className="unit-btn unit-btn-primary text-decoration-none">
            Back to List
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="unit-page acct-reportdetail-theme">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Accounts / Expense Report</div>
          <h2>{r.title}</h2>
          <p>
            Requester: {r.requester_name || "-"} • Total: ₹{totalAmount}
          </p>
        </div>

        <div className="unit-header-actions">
          <Link
            to="/admin/accounts/my-reports"
            className="unit-btn unit-btn-light text-decoration-none"
          >
            Back
          </Link>

          {canEdit && (
            <button
              className="unit-btn unit-btn-primary"
              onClick={submitClick}
              disabled={items.length === 0 || submitting}
            >
              {submitting ? "Submitting..." : "Submit to Manager"}
            </button>
          )}
        </div>
      </div>

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <span>Status</span>
          <strong
            className={`acct-rd-status-value acct-rd-status-value--${statusTone(
              r.status_code
            )}`}
          >
            {statusLabel(r.status_code)}
          </strong>
        </div>

        <div className="unit-stat-card">
          <span>Total Amount</span>
          <strong>₹{totalAmount}</strong>
        </div>

        <div className="unit-stat-card">
          <span>Line Items</span>
          <strong>{items.length}</strong>
        </div>

        <div className="unit-stat-card">
          <span>Receipts</span>
          <strong>{files.length}</strong>
        </div>

        <div className="unit-stat-card">
          <span>History</span>
          <strong>{history.length}</strong>
        </div>
      </div>

      <div className="unit-card mb-4">
        <div className="unit-toolbar">
          <div>
            <h5>Line Items</h5>
            <p>Add, review and delete expense line items.</p>
          </div>

          <span
            className={`unit-status acct-rd-chip acct-rd-chip--${
              canEdit ? "green" : "gray"
            }`}
          >
            {canEdit ? "Editable" : "Read Only"}
          </span>
        </div>

        {canEdit && (
          <div className="p-3 border-bottom">
            <ItemEditor
              item={item}
              setItem={setItem}
              onAdd={addItemClick}
              adding={adding}
            />
          </div>
        )}

        <div className="unit-table-wrapper acct-rd-items-wrapper">
          <table className="table unit-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Vendor</th>
                <th>Description</th>
                <th>Amount</th>
                <th className="text-end" style={{ width: "100px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {items.length > 0 ? (
                items.map((it) => (
                  <tr key={it.id}>
                    <td>
                      {it.expense_date
                        ? dayjs(it.expense_date).format("DD MMM")
                        : "-"}
                    </td>

                    <td>
                      <span
                        className={`unit-status acct-rd-chip acct-rd-chip--${categoryTone(
                          it.category
                        )}`}
                        title={it.category || "-"}
                      >
                        {it.category || "-"}
                      </span>
                    </td>

                    <td>{it.vendor_name || "-"}</td>

                    <td
                      style={{
                        maxWidth: "320px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={it.description || ""}
                    >
                      {it.description || "-"}
                    </td>

                    <td>
                      <span className="unit-id">
                        ₹{Number(it.amount || 0).toFixed(2)}
                      </span>
                    </td>

                    <td>
                      {canEdit && (
                        <div className="unit-actions">
                          <button
                            className="unit-icon-btn unit-delete"
                            onClick={() => deleteItemClick(it.id)}
                            title="Delete"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">
                    <div className="unit-empty">
                      <div className="unit-empty-icon">□</div>
                      <h5>No items yet</h5>
                      <p>Add your first expense line item.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="unit-card mb-4">
        <div className="unit-toolbar">
          <div>
            <h5>Receipts</h5>
            <p>Upload receipts, preview files and attach them to this report.</p>
          </div>
        </div>

        <div className="p-3">
          <DropZone disabled={!canEdit} onFile={(file) => onFileSelect(file)} />

          {previewFile && (
            <div className="unit-card mt-3" style={{ boxShadow: "none" }}>
              <div className="p-3">
                <div className="row g-3">
                  <div className="col-md-5">
                    {previewFile.type.startsWith("image/") ? (
                      <img
                        src={previewUrl}
                        alt="preview"
                        className="img-fluid rounded border"
                        style={{ maxHeight: 360, objectFit: "contain" }}
                      />
                    ) : (
                      <object
                        data={previewUrl}
                        type="application/pdf"
                        width="100%"
                        height="360"
                        className="border rounded w-100"
                      >
                        <div className="p-3 text-center">
                          <div className="fw-bold mb-2">PDF preview not available</div>
                          <a
                            href={previewUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="unit-btn unit-btn-primary text-decoration-none"
                          >
                            Open PDF
                          </a>
                        </div>
                      </object>
                    )}
                  </div>

                  <div className="col-md-7">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <h6 className="mb-1">{previewFile.name}</h6>
                        <p className="text-muted mb-0">
                          {prettyBytes(previewFile.size)} • {previewFile.type}
                        </p>
                      </div>

                      {checking && (
                        <span className="unit-status acct-rd-chip acct-rd-chip--blue">
                          Checking...
                        </span>
                      )}

                      {!checking && checkResult && (
                        <span
                          className={`unit-status acct-rd-chip acct-rd-chip--${
                            checkResult.ok ? "green" : "amber"
                          }`}
                        >
                          {checkResult.ok ? "Looks Good" : "Needs Attention"}
                        </span>
                      )}
                    </div>

                    {!checking && checkResult && (
                      <div className="mt-3">
                        <p className="mb-1">
                          <strong>Detected:</strong>
                        </p>

                        <p className="text-muted mb-2">
                          Date: {checkResult.fields?.date || "-"} • Amount:{" "}
                          {checkResult.fields?.amount ?? "-"} • Vendor:{" "}
                          {checkResult.fields?.vendor || "-"}
                          {typeof checkResult.ocr_confidence === "number" &&
                            ` • OCR: ${checkResult.ocr_confidence}%`}
                        </p>

                        {checkResult.issues?.length > 0 && (
                          <ul className="text-danger mb-0">
                            {checkResult.issues.map((issue, index) => (
                              <li key={index}>{issue}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    <div className="unit-actions mt-3">
                      <button
                        className="unit-btn unit-btn-primary"
                        onClick={attachReceipt}
                        disabled={attaching || checking}
                      >
                        {attaching ? "Attaching..." : "Attach Receipt"}
                      </button>

                      <button
                        className="unit-btn unit-btn-light"
                        onClick={clearPreview}
                        disabled={attaching}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="unit-table-wrapper p-0 mt-3 acct-rd-files-wrapper">
            <table className="table unit-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th className="text-end" style={{ width: "240px" }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {files.length > 0 ? (
                  files.map((file) => {
                    const ext = (file.file_name || "").split(".").pop()?.toLowerCase();
                    const isPdf =
                      (file.mime_type || "").includes("pdf") || ext === "pdf";

                    return (
                      <tr key={file.id}>
                        <td>
                          <div className="unit-name-box">
                            <div className="unit-letter">
                              {isPdf ? "P" : "I"}
                            </div>

                            <div>
                              <strong>{file.file_name}</strong>
                              <small>{file.mime_type || "-"}</small>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className={`unit-status acct-rd-chip acct-rd-chip--${
                              isPdf ? "purple" : "blue"
                            }`}
                          >
                            {isPdf ? "PDF" : "Image"}
                          </span>
                        </td>

                        <td>{file.file_size_bytes ? prettyBytes(file.file_size_bytes) : "-"}</td>

                        <td>
                          <div className="unit-actions">
                            <button
                              className="unit-btn unit-btn-light"
                              onClick={() =>
                                setModal({
                                  open: true,
                                  url: file.storage_path,
                                  name: file.file_name,
                                  type: isPdf ? "pdf" : "image",
                                })
                              }
                            >
                              Preview
                            </button>

                            <a
                              className="unit-btn unit-btn-light text-decoration-none"
                              href={file.storage_path}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open
                            </a>

                            <a
                              className="unit-btn unit-btn-light text-decoration-none"
                              href={file.storage_path}
                              download
                            >
                              Download
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">□</div>
                        <h5>No receipts yet</h5>
                        <p>Upload a receipt to attach it with this report.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>History</h5>
            <p>Track approval and status activity for this report.</p>
          </div>
        </div>

        <div className="p-3">
          {history.length > 0 ? (
            <ol className="mb-0">
              {history.map((h) => {
                const time = h.changed_at || h.created_at || null;

                return (
                  <li key={h.id} className="mb-2">
                    <span
                      className={`unit-status acct-rd-chip acct-rd-chip--${statusTone(
                        getHistStatus(h)
                      )}`}
                    >
                      {getHistStatus(h)}
                    </span>
                    <span className="text-muted">
                      {time ? dayjs(time).format("DD MMM, HH:mm") : "-"}
                    </span>
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="unit-empty">
              <div className="unit-empty-icon">□</div>
              <h5>No history yet</h5>
              <p>Status history will appear here.</p>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={modal.open}
        onHide={() => setModal({ open: false, url: null, name: "", type: "" })}
        size="xl"
        centered
        className="unit-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modal.name}</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ height: "75vh" }}>
          {modal.type === "image" ? (
            <img
              src={modal.url}
              alt={modal.name}
              className="h-100 w-100 rounded border"
              style={{ objectFit: "contain" }}
            />
          ) : (
            <object
              data={modal.url}
              type="application/pdf"
              width="100%"
              height="100%"
              className="border rounded"
            >
              <div className="p-3 text-center">
                <div className="fw-bold mb-2">PDF preview not available</div>
                <a
                  href={modal.url}
                  target="_blank"
                  rel="noreferrer"
                  className="unit-btn unit-btn-primary text-decoration-none"
                >
                  Open in new tab
                </a>
              </div>
            </object>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

function ItemEditor({ item, setItem, onAdd, adding }) {
  const isValid = item.expense_date && Number(item.amount) > 0;

  return (
    <div className="row g-3">
      <div className="col-md-2">
        <label className="form-label">Date</label>
        <input
          type="date"
          className="form-control"
          value={item.expense_date}
          onChange={(e) => setItem({ ...item, expense_date: e.target.value })}
        />
      </div>

      <div className="col-md-2">
        <label className="form-label">Category</label>
        <select
          className="form-select"
          value={item.category}
          onChange={(e) => setItem({ ...item, category: e.target.value })}
        >
          <option>Travel</option>
          <option>Hotel</option>
          <option>Food</option>
          <option>Local</option>
          <option>Misc</option>
        </select>
      </div>

      <div className="col-md-2">
        <label className="form-label">Amount</label>
        <input
          type="number"
          className="form-control"
          value={item.amount}
          onChange={(e) => setItem({ ...item, amount: e.target.value })}
        />
      </div>

      <div className="col-md-3">
        <label className="form-label">Vendor</label>
        <input
          className="form-control"
          value={item.vendor_name}
          onChange={(e) => setItem({ ...item, vendor_name: e.target.value })}
        />
      </div>

      <div className="col-md-3">
        <label className="form-label">Description</label>
        <input
          className="form-control"
          value={item.description}
          onChange={(e) => setItem({ ...item, description: e.target.value })}
        />
      </div>

      <div className="col-12">
        <button
          className="unit-btn unit-btn-primary"
          onClick={onAdd}
          disabled={!isValid || adding}
        >
          {adding ? "Adding..." : "+ Add Item"}
        </button>
      </div>
    </div>
  );
}

function DropZone({ disabled, onFile }) {
  const [drag, setDrag] = useState(false);

  return (
    <div
      className="unit-card"
      style={{
        boxShadow: "none",
        borderStyle: "dashed",
        background: drag ? "#f8fafc" : "#ffffff",
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);

        if (disabled) return;

        const file = e.dataTransfer.files?.[0];
        if (file) onFile(file);
      }}
    >
      <div className="p-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h6 className="mb-1">Upload Receipt</h6>
          <p className="text-muted mb-0">
            Drag & drop a receipt here, or choose a file. PNG/JPG/PDF, up to 8MB.
          </p>
        </div>

        <label
          className={`unit-btn unit-btn-primary mb-0 ${
            disabled ? "disabled" : ""
          }`}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        >
          Choose File
          <input
            type="file"
            accept="image/*,.pdf"
            className="d-none"
            disabled={disabled}
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          />
        </label>
      </div>
    </div>
  );
}